/* eslint-disable ember/classic-decorator-hooks, ember/no-computed-properties-in-native-classes */
import EmberObject, { computed, set } from '@ember/object';

import FilterConstants from '../../filter-constants';
import * as CommonUtils from '../../utils/common';
import OpAmpSelection from '../component-selector/op-amp-selection';
import Implementation from '../implementation/implementation';
import AllowedLimits from '../optimization/allowed-specific';
import RecommendedLimits from '../optimization/recommended-specific';
import CircuitResults from '../results/circuit-results';

export default class ActualStage extends EmberObject {
  targetStage;
  previousStage;
  nextStage;
  opAmpSource;
  whoAmI = 'app/models/stages/actual-stage.js';
  filter;
  timeAxis;
  filterFrequencyRange;
  frequencyArray;
  stageLetter;
  order;
  stageId;
  opAmp;
  compensateLoop;
  selectedImplementation;
  optimization;
  masterCapacitorValue;
  r3Value;
  stopbandFrequency;
  componentsInput;

  init() {
    if (!CommonUtils.isNullOrUndefined(this.targetStage)) {
      this.filter = this.targetStage.filter;
      this.timeAxis = this.targetStage.timeAxis;
      set(this, 'filterFrequencyRange', this.targetStage.filterFrequencyRange);
      this.stageLetter = this.targetStage.stageLetter;
      this.order = this.targetStage.order;
      this.stageId = this.targetStage.stageId;
    }

    if (CommonUtils.isNullOrUndefined(this.frequencyArray))
      set(this, 'frequencyArray', this.targetStage.frequencyArray);
  }

  @computed('selectedImplementation', 'optimization', 'targetStage', 'componentsInput') get implementation() {
    var selectedImplementation = this.selectedImplementation;
    var filter = this.targetStage.filter;
    var optimization = this.optimization;
    var stageFp = this.targetStage.stageFp;
    var stageQ = this.targetStage.stageQ;
    var gain = this.targetStage.gain;
    var frequencyArray = this.targetStage.frequencyArray;
    var preferredAlgorithm = this.componentsInput ? this.componentsInput.preferredAlgorithms : null;

    var result = Implementation.create({
      preferredAlgorithm: preferredAlgorithm,
      selectedImplementation: selectedImplementation,
      filter: filter,
      optimization: optimization,
      stageFp: stageFp,
      stageQ: stageQ,
      gain: gain,
      frequencyArray: frequencyArray,
    });

    return result;
  }

  @computed(
    'implementation',
    'filterFrequencyRange',
    'opAmp',
    'masterCapacitorValue',
    'r3Value',
    'compensateLoop',
    'opAmpSource',
    'componentsInput',
  )
  get actualStageValues() {
    var filter = this.implementation.filter;
    var optimization = this.optimization;
    var filterFrequencyRange = this.filterFrequencyRange;
    var loopFp = this.implementation.stageFp;
    var loopQ = this.implementation.stageQ;
    var targetFp = this.implementation.stageFp;
    var targetQ = this.implementation.stageQ;
    var gain = this.implementation.gain;
    var opAmp = this.opAmp;
    var compensateLoop = this.compensateLoop;
    var actualFpGainAndQ = null;
    var circuit = null;
    var implementation = this.implementation;
    var isSpecific = this.componentsInput
      ? this.componentsInput.optimizationPreference === FilterConstants.optimizationPreferenceOptions.specific
      : false;
    var compensateLoopRun = 0;

    var circuitResult = null;
    var newFrequencyArray = null;
    do {
      // TODO: ARNA-4965 Temporary workaround for different implementations of getActualStageSpecFrequency
      // bandPass uses filterFrequencyRange, lowPass and highPass uses loopFp
      var actualStageFreqParam = filter.name === FilterConstants.filterType.bandPass ? filterFrequencyRange : loopFp;
      var newFrequencyPointsObject = filter.getActualStageSpecFrequency(actualStageFreqParam);
      newFrequencyArray = newFrequencyPointsObject.narrowFrequencyArray;
      circuit = implementation.createCircuit(loopFp, gain, loopQ, optimization);

      circuitResult = CircuitResults.create({
        circuit: circuit,
        actualStage: this,
        adcBandwidth: null,
        frequencyArray: newFrequencyArray,
      });

      this._setOpAmp(circuit, opAmp, circuitResult);
      actualFpGainAndQ = circuitResult.stageValues;

      loopFp = Math.pow(targetFp / actualFpGainAndQ.fp, 0.8) * loopFp;
      loopQ = Math.pow(targetQ / actualFpGainAndQ.q, 0.8) * loopQ;
      compensateLoopRun++;
    } while (compensateLoopRun < compensateLoop);

    if (isSpecific) {
      circuit.changeInitialCapacitor(this.masterCapacitorValue);
      circuit.changeGainValues(this.r3Value);
      let afterAdjustmentCircuitResult = CircuitResults.create({
        circuit: circuit,
        actualStage: this,
        adcBandwidth: null,
        frequencyArray: newFrequencyArray,
      });

      actualFpGainAndQ = afterAdjustmentCircuitResult.stageValues;
    }

    var result = {
      fp: actualFpGainAndQ.fp,
      q: actualFpGainAndQ.q,
      gain: actualFpGainAndQ.gain,
      circuit: circuit,
    };

    return result;
  }

  @computed('actualStageValues') get stageFp() {
    var actualStageValues = this.actualStageValues;
    var result = actualStageValues.fp;
    return result;
  }

  @computed('actualStageValues') get stageQ() {
    var actualStageValues = this.actualStageValues;
    var result = actualStageValues.q;
    return result;
  }

  @computed('actualStageValues', 'implementation') get gain() {
    var actualStageValues = this.actualStageValues;
    var result = actualStageValues.gain;
    return result;
  }

  @computed('previousStage', 'actualStageValues', 'frequencyArray') get circuit() {
    var result = this.actualStageValues.circuit;
    var previousCircuit = this.previousStage ? this.previousStage.circuit : null;
    var frequencyArray = this.frequencyArray;
    set(result, 'frequencyArray', frequencyArray);
    set(result, 'previousCircuit', previousCircuit);

    return result;
  }

  @computed('circuit') get magnitude() {
    var circuit = this.circuit;
    var frequencyArray = this.frequencyArray;
    var circuitResult = CircuitResults.create({
      circuit: circuit,
      actualStage: this,
      adcBandwidth: null,
      frequencyArray: frequencyArray,
    });
    var result = circuitResult.magnitude;
    return result;
  }

  _setOpAmp(circuit, opAmp, circuitResult) {
    if (opAmp !== FilterConstants.idealOpAmp) {
      var opAmpData = this.opAmpSource.getOpAmp(opAmp);
      var opAmpObj = OpAmpSelection.create({ data: opAmpData });
      set(circuit, 'opAmp', opAmpObj);
      set(circuit, 'recommendedAmp', true);
    } else {
      var idealOpAmp = circuitResult.idealOpAmp;
      set(circuit, 'opAmp', idealOpAmp);
    }
  }

  @computed(
    'targetStage',
    'implementation',
    'stopbandFrequency',
    'frequencyArray',
    'masterCapacitorValue',
    'r3Value',
    'opAmp',
  )
  get allowedLimits() {
    var implementation = this.implementation;
    var stopbandFrequency = this.stopbandFrequency;
    var frequencyArray = this.frequencyArray;
    var optimization = AllowedLimits.create({ stopbandFrequency: stopbandFrequency });
    var stageFp = this.targetStage.stageFp;
    var stageQ = this.targetStage.stageQ;
    var gain = this.targetStage.gain;
    var circuit = implementation.createCircuit(stageFp, gain, stageQ, optimization, this.masterCapacitorValue);
    circuit.changeGainValues(this.r3Value);
    var circuitResult = CircuitResults.create({
      circuit: circuit,
      actualStage: this,
      adcBandwidth: null,
      frequencyArray: frequencyArray,
    });
    var result = circuitResult.idealOpAmp;
    return result;
  }

  @computed(
    'targetStage',
    'implementation',
    'stopbandFrequency',
    'frequencyArray',
    'masterCapacitorValue',
    'r3Value',
    'opAmp',
  )
  get recommendedLimits() {
    var implementation = this.implementation;
    var stopbandFrequency = this.stopbandFrequency;
    var frequencyArray = this.frequencyArray;
    var optimization = RecommendedLimits.create({ stopbandFrequency: stopbandFrequency });
    var stageFp = this.targetStage.stageFp;
    var stageQ = this.targetStage.stageQ;
    var gain = this.targetStage.gain;
    var circuit = implementation.createCircuit(stageFp, gain, stageQ, optimization, this.masterCapacitorValue);
    circuit.changeGainValues(this.r3Value);
    var circuitResult = CircuitResults.create({
      circuit: circuit,
      actualStage: this,
      adcBandwidth: null,
      frequencyArray: frequencyArray,
    });
    var result = circuitResult.idealOpAmp;
    return result;
  }

  @computed('implementation', 'targetStage', 'previousStage', 'optimization', 'masterCapacitorValue')
  get highVNoiseCircuit() {
    var implementation = this.implementation;
    var stageFp = this.targetStage.stageFp;
    var stageQ = this.targetStage.stageQ;
    var gain = this.targetStage.gain;
    var optimization = this.optimization;
    var result = implementation.createCircuit(stageFp, gain, stageQ, optimization, this.masterCapacitorValue);
    var highVNoiseOpAmp = { gbw: 1e12, rOut: 1e-12, vNoise: 1e-6, iNoise: 0 };
    var previousHighVNoiseCircuit = this.previousStage ? this.previousStage.highVNoiseCircuit : null;
    set(result, 'opAmp', highVNoiseOpAmp);
    set(result, 'previousCircuit', previousHighVNoiseCircuit);

    return result;
  }

  @computed('implementation', 'targetStage', 'previousStage', 'optimization', 'masterCapacitorValue')
  get highINoiseCircuit() {
    var implementation = this.implementation;
    var stageFp = this.targetStage.stageFp;
    var stageQ = this.targetStage.stageQ;
    var gain = this.targetStage.gain;
    var optimization = this.optimization;
    var result = implementation.createCircuit(stageFp, gain, stageQ, optimization, this.masterCapacitorValue);
    var highINoiseOpAmp = { gbw: 1e12, rOut: 1e-12, vNoise: 0, iNoise: 1e-6 };
    var previousHighINoiseCircuit = this.previousStage ? this.previousStage.highINoiseCircuit : null;
    set(result, 'opAmp', highINoiseOpAmp);
    set(result, 'previousCircuit', previousHighINoiseCircuit);

    return result;
  }
}
